import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { type PartyRef, PAYMENT_METHOD_LABEL, PAYMENT_METHODS, type PaymentMethod, todayIso } from '@reikraft/shared';
import { Field, Modal, useToast } from '@reikraft/ui';
import { api, qs } from '../api';
import { formatDate, formatMoney, kopToRub, rubToKop } from '../format';
import { useFlag, useMe, useTz } from '../session';
import { FormMessage, Hint, useFormErrors, useStaff } from './common';
import { PartyPicker, type PartyValue } from './PartyPicker';

interface Unpaid {
  id: string;
  number: string;
  totalKop: number;
  paidKop: number;
  issuedAt: string | null;
}

/** Приём оплаты: сумма разносится по неоплаченным заказам клиента */
export function PaymentModal({
  open,
  onClose,
  party: initialParty,
  defaultOrderId,
  defaultAmountKop,
}: {
  open: boolean;
  onClose: () => void;
  party: PartyRef | null;
  defaultOrderId?: string;
  defaultAmountKop?: number;
}) {
  const me = useMe();
  const tz = useTz();
  const npd = useFlag('npd');
  const staff = useStaff();
  const qc = useQueryClient();
  const toast = useToast();
  const errs = useFormErrors();
  const [party, setParty] = useState<PartyValue | null>(null);
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<PaymentMethod>('transfer');
  const [paidAt, setPaidAt] = useState(todayIso(tz));
  const [receivedById, setReceivedById] = useState(me.id);
  const [payerInn, setPayerInn] = useState('');
  const [receiptRequired, setReceiptRequired] = useState(true);
  const [comment, setComment] = useState('');
  const [alloc, setAlloc] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open) return;
    setParty(initialParty ? { kind: initialParty.kind, id: initialParty.id, name: initialParty.name } : null);
    setAmount(kopToRub(defaultAmountKop ?? null));
    setAlloc(defaultOrderId && defaultAmountKop ? { [defaultOrderId]: kopToRub(defaultAmountKop) } : {});
    setPaidAt(todayIso(tz));
    setComment('');
    setPayerInn('');
    errs.clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const partyId = party && party.kind !== 'new' ? party.id : null;
  const unpaid = useQuery({
    queryKey: ['unpaid', party?.kind, partyId],
    queryFn: () => api.get<Unpaid[]>(`/admin/unpaid${qs(party?.kind === 'organization' ? { orgId: partyId } : { customerId: partyId })}`),
    enabled: open && !!partyId,
  });

  const amountKop = rubToKop(amount) ?? 0;
  const allocated = Object.values(alloc).reduce((s, v) => s + (rubToKop(v) ?? 0), 0);

  const save = useMutation({
    mutationFn: () =>
      api.post('/admin/payments', {
        customerId: party?.kind === 'customer' ? party.id : null,
        organizationId: party?.kind === 'organization' ? party.id : null,
        amountKop,
        method,
        paidAt,
        receivedById,
        payerInn: payerInn || null,
        comment: comment || null,
        receiptRequired,
        allocations: Object.entries(alloc)
          .map(([orderId, v]) => ({ orderId, amountKop: rubToKop(v) ?? 0 }))
          .filter((a) => a.amountKop > 0),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['order'] });
      void qc.invalidateQueries({ queryKey: ['orders'] });
      void qc.invalidateQueries({ queryKey: ['payments'] });
      void qc.invalidateQueries({ queryKey: ['finance'] });
      void qc.invalidateQueries({ queryKey: ['counters'] });
      void qc.invalidateQueries({ queryKey: ['unpaid'] });
      toast(npd && receiptRequired ? 'Оплата записана. Не забудьте выбить чек в «Мой налог»' : 'Оплата записана');
      onClose();
    },
    onError: (e) => errs.set(e),
  });

  const autoAllocate = () => {
    let left = amountKop;
    const next: Record<string, string> = {};
    for (const u of unpaid.data ?? []) {
      if (left <= 0) break;
      const take = Math.min(left, u.totalKop - u.paidKop);
      next[u.id] = kopToRub(take);
      left -= take;
    }
    setAlloc(next);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Принять оплату"
      wide
      footer={
        <>
          <button type="button" className="rk-btn" onClick={onClose}>
            Отмена
          </button>
          <button
            type="button"
            className="rk-btn rk-btn--primary"
            disabled={save.isPending || !amountKop || !partyId || allocated > amountKop}
            onClick={() => save.mutate()}
          >
            Записать {amountKop ? formatMoney(amountKop) : ''}
          </button>
        </>
      }
    >
      <FormMessage message={errs.message} />
      {!initialParty && <PartyPicker value={party} onChange={setParty} allowNew={false} />}
      {initialParty && (
        <div>
          <span className="rk-muted">От кого:</span> <b>{initialParty.name}</b>
        </div>
      )}
      <div className="rk-grid-form" style={{ ['--rk-min' as string]: '160px' }}>
        <Field label="Сумма, ₽" error={errs.errors.amountKop}>
          <input
            className="rk-input rk-input--mono"
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            autoFocus
          />
        </Field>
        <Field label="Способ">
          <select className="rk-select" value={method} onChange={(e) => setMethod(e.target.value as PaymentMethod)}>
            {PAYMENT_METHODS.map((m) => (
              <option key={m} value={m}>
                {PAYMENT_METHOD_LABEL[m]}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Дата">
          <input className="rk-input rk-input--mono" type="date" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} />
        </Field>
        <Field label="Кто получил деньги" hint={npd ? 'Он выбивает чек' : undefined}>
          <select className="rk-select" value={receivedById} onChange={(e) => setReceivedById(e.target.value)}>
            {(staff.data ?? [])
              .filter((s) => s.isActive)
              .map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
          </select>
        </Field>
        {party?.kind === 'organization' && (
          <Field label="ИНН плательщика" hint="Для чека юрлицу; по умолчанию — из карточки организации">
            <input className="rk-input rk-input--mono" value={payerInn} onChange={(e) => setPayerInn(e.target.value)} />
          </Field>
        )}
      </div>
      {partyId && (
        <div className="rk-stack" style={{ ['--rk-gap' as string]: '8px' }}>
          <div className="rk-row" style={{ justifyContent: 'space-between' }}>
            <span className="rk-label">За какие заказы</span>
            {(unpaid.data?.length ?? 0) > 0 && (
              <button type="button" className="rk-btn rk-btn--sm rk-btn--link" onClick={autoAllocate}>
                Разнести по старым заказам
              </button>
            )}
          </div>
          {(unpaid.data ?? []).map((u) => (
            <div
              key={u.id}
              className="rk-row"
              style={{ justifyContent: 'space-between', borderBottom: '1px dashed var(--line-2)', paddingBottom: 6 }}
            >
              <span>
                <b className="rk-mono">{u.number}</b>{' '}
                <span className="rk-muted">
                  · долг {formatMoney(u.totalKop - u.paidKop)}
                  {u.issuedAt ? ` · выдан ${formatDate(u.issuedAt, tz)}` : ''}
                </span>
              </span>
              <input
                className="rk-input rk-input--mono"
                style={{ width: 130, height: 36 }}
                inputMode="decimal"
                aria-label={`Сумма по заказу ${u.number}`}
                value={alloc[u.id] ?? ''}
                placeholder="0"
                onChange={(e) => setAlloc({ ...alloc, [u.id]: e.target.value })}
              />
            </div>
          ))}
          {unpaid.data && !unpaid.data.length && <Hint>Неоплаченных заказов нет — оплата сохранится как аванс.</Hint>}
          {allocated > amountKop && <div className="rk-field__error">Разнесено больше, чем пришло</div>}
        </div>
      )}
      {npd && (
        <label className="rk-check">
          <input type="checkbox" checked={receiptRequired} onChange={(e) => setReceiptRequired(e.target.checked)} />
          Нужен чек «Мой налог»{method === 'transfer' ? ' — за перевод до 9-го числа следующего месяца' : ' — в день оплаты'}
        </label>
      )}
      <Field label="Комментарий">
        <input className="rk-input" value={comment} onChange={(e) => setComment(e.target.value)} />
      </Field>
    </Modal>
  );
}
